import React, { useState } from "react";
import {
  Box,
  TextField,
  Button,
  MenuItem,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Card,
  CardContent,
  CardMedia,
} from "@mui/material";
import { BookOpen, PlusCircle, Trash2 } from "lucide-react";
import { toast } from "react-hot-toast";

const BlogCreation = () => {
  const [formData, setFormData] = useState({
    title: "",
    category: "",
    source: "",
    image: "",
    galleryImages: [],
    description: "",
  });

  const [keyword, setKeyword] = useState("");
  const [keywordsList, setKeywordsList] = useState([]);
  const [openPreview, setOpenPreview] = useState(false);
  const role = sessionStorage.getItem("role");
  const token = sessionStorage.getItem("accesstoken");

  const categories = [
    "Income Tax",
    "TaxUpdates NG",
    "GST (Goods and Services Tax)",
    "Professional Tax",
    "TDS (Tax Deducted at Source)",
    "TCS (Tax Collected at Source)",
  ];

  const handleAddKeyword = () => {
    if (keyword.trim() && !keywordsList.includes(keyword)) {
      setKeywordsList([...keywordsList, keyword]);
      setKeyword("");
    }
  };

  const handleRemoveKeyword = (kw) => {
    setKeywordsList(keywordsList.filter((item) => item !== kw));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleGalleryImagesChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 4) {
      toast.error("You can only upload up to 4 images for the gallery.");
      return;
    }
    setFormData({ ...formData, galleryImages: files });
  };

  const galleryPreviewsImages = formData.galleryImages.map((file) =>
    URL.createObjectURL(file)
  );

  const handleSubmit = async () => {
    const BlogData = new FormData();

    BlogData.append("title", formData.title);
    BlogData.append("category", formData.category);
    BlogData.append("source", formData.source);
    BlogData.append("description", formData.description);
    BlogData.append("keywords", JSON.stringify(keywordsList));
    BlogData.append("role", role);
    BlogData.append("author", "Nilesh Gajjar");

    if (formData.image) {
      BlogData.append("image", formData.image);
    }

    formData.galleryImages.forEach((file) => {
      BlogData.append("gallery", file);
    });

    if (formData.source === "") {
      formData.source = "Rohans Tax Services";
    }

    try {
      const response = await fetch(
        "https://rohan-tax-services.onrender.com/api/auth/createblog",
        {
          method: "POST",
          headers: {
            Authorization: `${token}`,
          },
          body: BlogData,
        }
      );

      if (response.ok) {
        toast.success("Blog created successfully 😊!");
        setFormData({
          title: "",
          category: "",
          source: "",
          image: "",
          galleryImages: [],
          description: "",
        });
        setKeywordsList([]);
        setKeyword("");
        setOpenPreview(false);
      } else {
        const errorData = await response.json();
        toast.error(`An error occurred: ${errorData.error || "Unknown error"}`);
      }
    } catch (error) {
      console.error("An error occurred during the API call:", error);
      toast.error("An error occurred while creating the blog!");
    }
  };

  const handlePreview = () => {
    setOpenPreview(true);
  };

  const handleClosePreview = () => {
    setOpenPreview(false);
  };

  const imagePreview = formData.image
    ? URL.createObjectURL(formData.image)
    : null;

  const galleryPreviews = formData.galleryImages.map((file) =>
    URL.createObjectURL(file)
  );

  return (
    <Box
      className="bg-white rounded-lg shadow-lg"
      sx={{
        flexGrow: 1,
        width: { xs: "100%", sm: "90%", md: "80%" }, // Responsive width
        maxWidth: "100%",
        boxSizing: "border-box",
        padding: { xs: 2, sm: 4, md: 6 }, // Responsive padding
        boxShadow: { xs: 2, sm: 4, md: 6 }, // Responsive shadow depth
        margin: "0 auto", // Center horizontally
      }}
    >
      <h1 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-2">
        <BookOpen size={24} />
        Create Blog
      </h1>

      <Box className="space-y-6">
        <TextField
          label="Title"
          name="title"
          value={formData.title}
          onChange={handleChange}
          fullWidth
          className="bg-gray-100"
          variant="outlined"
        />

        <Box>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Keywords
          </label>
          <Box className="flex items-center gap-2">
            <TextField
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="Add a keyword"
              className="bg-gray-100"
              fullWidth
              variant="outlined"
            />
            <IconButton
              onClick={handleAddKeyword}
              color="primary"
              size="large"
              className="bg-blue-600 text-white hover:bg-blue-700"
            >
              <PlusCircle />
            </IconButton>
          </Box>
          <Box className="mt-2 flex flex-wrap gap-2">
            {keywordsList.map((kw, index) => (
              <Box
                key={index}
                className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full flex items-center gap-2"
              >
                {kw}
                <Trash2
                  size={16}
                  className="cursor-pointer text-red-600 hover:text-red-800"
                  onClick={() => handleRemoveKeyword(kw)}
                />
              </Box>
            ))}
          </Box>
        </Box>

        <TextField
          label="Category"
          name="category"
          value={formData.category}
          onChange={handleChange}
          select
          fullWidth
          className="bg-gray-100"
          variant="outlined"
        >
          {categories.map((cat) => (
            <MenuItem key={cat} value={cat}>
              {cat}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          label="Source"
          name="source"
          value={formData.source}
          onChange={handleChange}
          fullWidth
          className="bg-gray-100"
          variant="outlined"
        />

        <Box className="flex items-center gap-6">
          <label className="block font-medium text-gray-700">Main Image:</label>
          <input
            type="file"
            name="image"
            onChange={(e) =>
              setFormData({ ...formData, image: e.target.files[0] })
            }
            className="block w-full text-gray-500"
          />
        </Box>

        <Box className="flex flex-col gap-2">
          <label className="block font-medium text-gray-700">
            Gallery Images (Max 4):
          </label>
          <input
            type="file"
            name="galleryImages"
            multiple
            accept="image/*"
            onChange={handleGalleryImagesChange}
            className="block w-full text-gray-500"
          />
          <Box className="flex md:flex-row flex-col md:mx-0 mx-auto gap-2 mt-2">
            {galleryPreviews.map((src, index) => (
              <img
                key={index}
                src={src}
                alt={`Gallery Preview ${index + 1}`}
                className="w-24 h-24 object-cover rounded-lg shadow-md"
              />
            ))}
          </Box>
        </Box>

        <TextField
          label="Description"
          name="description"
          value={formData.description}
          onChange={handleChange}
          multiline
          rows={6}
          fullWidth
          className="bg-gray-100"
          variant="outlined"
          sx={{ whiteSpace: "pre-wrap", wordWrap: "break-word" }}
        />

        <Box className="flex items-center gap-4">
          <Button
            variant="contained"
            color="secondary"
            onClick={handlePreview}
            className="bg-yellow-600 text-white hover:bg-yellow-700"
          >
            Preview Blog
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            className="bg-blue-600 text-white hover:bg-blue-700"
          >
            Submit Blog
          </Button>
        </Box>
      </Box>

      {/* Preview Modal */}
      <Dialog open={openPreview} onClose={handleClosePreview}>
        <DialogTitle>Blog Preview</DialogTitle>
        <DialogContent>
          <Card sx={{ maxWidth: "100%" }}>
            {imagePreview && (
              <CardMedia
                component="img"
                height="140"
                image={imagePreview}
                alt="Uploaded Image"
              />
            )}
            {galleryPreviewsImages && galleryPreviewsImages.length > 0 && (
              <div className="gallery-container grid grid-cols-3 gap-4">
                {galleryPreviewsImages.map((src, index) => (
                  <CardMedia
                    key={index}
                    component="img"
                    image={src}
                    alt={`Gallery Image ${index + 1}`}
                  />
                ))}
              </div>
            )}
            <CardContent>
              <h3 className="font-bold">Title: {formData.title}</h3>
              <p>
                <strong>Category:</strong> {formData.category}
              </p>
              <p>
                <strong>Source:</strong> {formData.source}
              </p>
              <p style={{ whiteSpace: "pre-wrap" }}>
                <strong>Description:</strong> {formData.description}
              </p>
              <p>
                <strong>Keywords:</strong> {keywordsList.join(", ")}
              </p>
            </CardContent>
          </Card>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePreview} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BlogCreation;
